import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { timeAgo } from '../lib/utils'
import { useAccount } from '../context/AccountContext'

const LIMIT_OPTIONS = [50, 100, 200, 500]

// Statuses where the order could still fill/cancel on its own — everything else
// (filled, canceled, expired, rejected, ...) is terminal and goes in History.
const OPEN_STATUSES = new Set([
  'new', 'accepted', 'pending_new', 'partially_filled', 'pending_cancel',
  'pending_replace', 'calculated', 'held', 'accepted_for_bidding',
])

// Category drives the row's left-border accent and the P&L text color — the
// ONLY color signal in this table (side/status are intentionally plain gray;
// stacking side-color + status-color + category-color on one row competes for
// attention and stops meaning anything, per standard data-table UX guidance).
// 'active_buy'  — filled BUY, position still open: money sitting in a trade, unrealized.
// 'closed_win'  — filled SELL matched to a profitable trade_exits row: realized profit.
// 'closed_loss' — filled SELL matched to a losing trade_exits row: realized loss.
// null          — everything else (pending/canceled orders, or a filled BUY whose
//                 position has since fully closed) — no P&L is shown, not fabricated.
const CATEGORY_STYLE = {
  active_buy:  { border: '#3b82f6', text: '#60a5fa' },
  closed_win:  { border: '#334155', text: '#e2e8f0' },
  closed_loss: { border: '#f43f5e', text: '#fb7185' },
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  })
}

function fmtQty(n) {
  if (n == null) return '—'
  return Number(n).toFixed(2)
}

function fmtPrice(n) {
  if (n == null) return '—'
  return `$${Number(n).toFixed(2)}`
}

const TABS = [
  { key: 'all',     label: 'All' },
  { key: 'open',    label: 'Open Orders' },
  { key: 'history', label: 'Trade History' },
]

const THEAD_CELL = {
  padding: '8px 10px', color: '#475569', fontWeight: 600,
  letterSpacing: '0.06em', fontSize: '0.6rem', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, background: '#111827', zIndex: 1,
  borderBottom: '1px solid #1e293b',
}

export default function Trades() {
  // 2026-09-19: account selection now comes from the shared header switcher
  // (AccountContext) instead of a page-local toggle -- MAIN and Tier 2 are
  // both genuinely live (see docs/GOAL_RECOVER_TO_100K.md), and having a
  // second, independent toggle just on this page risked disagreeing with the
  // one in the header, showing different accounts on different pages at once.
  const { selectedAccount: account } = useAccount()
  const [orders, setOrders] = useState([])
  const [pnlBySymbol, setPnlBySymbol] = useState([])
  const [totalRealized, setTotalRealized] = useState(null)
  const [limit, setLimit] = useState(100)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastSync, setLastSync] = useState(null)
  const [tab, setTab] = useState('all')
  const [symbolFilter, setSymbolFilter] = useState('')
  const [sideFilter, setSideFilter] = useState('all')

  const fetchTrades = useCallback(async () => {
    try {
      const res = await api.trades(limit, account)
      if (res.success) {
        setOrders(res.orders || [])
        setPnlBySymbol(res.pnl_by_symbol || [])
        setTotalRealized(res.total_realized_pnl_14d ?? null)
        setError(null)
      } else {
        setError(res.error || 'Failed to load trades')
      }
    } catch (e) {
      setError('Unable to reach backend.')
    } finally {
      setLoading(false)
      setLastSync(new Date())
    }
  }, [limit, account])

  useEffect(() => {
    setLoading(true)
    fetchTrades()
    const id = setInterval(fetchTrades, 30_000)
    return () => clearInterval(id)
  }, [fetchTrades])

  const filtered = useMemo(() => {
    return orders.filter(o => {
      if (tab === 'open' && !OPEN_STATUSES.has(o.status)) return false
      if (tab === 'history' && OPEN_STATUSES.has(o.status)) return false
      if (sideFilter !== 'all' && o.side !== sideFilter) return false
      if (symbolFilter && !o.symbol.toLowerCase().includes(symbolFilter.toLowerCase())) return false
      return true
    })
  }, [orders, tab, sideFilter, symbolFilter])

  const columns = [
    'Asset', 'Order Type', 'Side', 'P&L', 'P&L %', 'Qty', 'Filled Qty',
    'Avg. Fill Price', 'Status', 'Submitted At', 'Filled At', 'Expires At',
  ]

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-slate-200 font-semibold text-sm tracking-widest"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            TRADES
          </h2>
          <p className="text-slate-500 text-xs mt-0.5">
            Alpaca order history ({account === 'tier2' ? 'Tier 2 — live trading account' : 'Main — live trading account'}) —
            blue = open position (unrealized), default = closed profit, red = closed loss.
            Switch accounts from the header.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {LIMIT_OPTIONS.map(n => (
              <button key={n} onClick={() => setLimit(n)}
                className="px-2.5 py-1 rounded text-xs font-mono transition-colors"
                style={{
                  background: limit === n ? 'rgba(34,211,238,0.15)' : 'transparent',
                  color:      limit === n ? '#67e8f9' : '#64748b',
                  border:     limit === n ? '1px solid rgba(34,211,238,0.4)' : '1px solid #1e293b',
                }}>
                {n}
              </button>
            ))}
          </div>
          <p className="text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
            {lastSync ? `SYNC ${timeAgo(lastSync)}` : 'LOADING…'}
          </p>
          <button onClick={fetchTrades}
            className="flex items-center gap-1.5 text-slate-600 hover:text-slate-300 border border-gray-800 rounded-lg px-3 py-1.5 hover:bg-gray-800 transition-colors"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}>
            <RefreshCw className="h-3 w-3" />
            REFRESH
          </button>
        </div>
      </div>

      {/* Tabs + filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em',
                background: tab === t.key ? '#1d4ed8' : '#111827',
                color:      tab === t.key ? '#bfdbfe' : '#64748b',
                border:     tab === t.key ? '1px solid rgba(59,130,246,0.4)' : '1px solid #374151',
              }}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter symbol…"
            value={symbolFilter}
            onChange={e => setSymbolFilter(e.target.value)}
            className="rounded-lg px-3 py-1.5 text-xs placeholder-slate-700 focus:outline-none"
            style={{
              background: '#111827', border: '1px solid #374151', color: '#e2e8f0',
              fontFamily: 'JetBrains Mono, monospace', width: '130px',
            }}
          />
          <div className="flex gap-1">
            {['all', 'buy', 'sell'].map(s => (
              <button key={s} onClick={() => setSideFilter(s)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium uppercase transition-all"
                style={{
                  fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em',
                  background: sideFilter === s ? '#1e293b' : '#111827',
                  color:      sideFilter === s ? '#e2e8f0' : '#64748b',
                  border:     '1px solid #374151',
                }}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* P&L by Ticker — realized P&L per symbol, same 14-day window used to
          match SELL orders below. Click a ticker to filter the table to it. */}
      {pnlBySymbol.length > 0 && (
        <div className="rounded-xl border border-gray-800 p-3" style={{ background: '#111827' }}>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-slate-500 font-mono uppercase tracking-widest" style={{ fontSize: '0.62rem' }}>
              P&amp;L by Ticker — realized, last 14 days
            </p>
            {totalRealized != null && (
              <p className="font-mono font-bold" style={{
                fontSize: '0.72rem',
                color: totalRealized >= 0 ? '#e2e8f0' : '#fb7185',
              }}>
                TOTAL {totalRealized >= 0 ? '+' : ''}${totalRealized.toFixed(2)}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {pnlBySymbol.map(s => {
              const positive = s.total_pnl_dollar >= 0
              const active = symbolFilter.toLowerCase() === s.symbol.toLowerCase()
              return (
                <button
                  key={s.symbol}
                  onClick={() => setSymbolFilter(active ? '' : s.symbol)}
                  title={`${s.trades} trade${s.trades === 1 ? '' : 's'} · ${s.win_rate}% win rate`}
                  className="rounded-lg px-3 py-1.5 text-left transition-colors"
                  style={{
                    background: active ? 'rgba(52,211,153,0.12)' : '#0b1220',
                    border: `1px solid ${active ? 'rgba(52,211,153,0.5)' : '#1e293b'}`,
                    borderLeft: `3px solid ${positive ? '#334155' : '#f43f5e'}`,
                    minWidth: '92px',
                  }}>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', fontWeight: 700, color: '#e2e8f0' }}>
                    {s.symbol}
                  </div>
                  <div style={{
                    fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', fontWeight: 700,
                    color: positive ? '#94a3b8' : '#fb7185',
                  }}>
                    {positive ? '+' : ''}${s.total_pnl_dollar.toFixed(2)}
                  </div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.58rem', color: '#475569' }}>
                    {s.trades}t · {s.win_rate}%
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg p-4 bg-rose-900/20 border border-rose-700/40 text-rose-300 text-sm font-mono">
          {error}
        </div>
      )}

      {loading && !orders.length && (
        <div className="text-slate-500 text-sm font-mono animate-pulse">Loading…</div>
      )}

      {!loading && !error && orders.length > 0 && filtered.length === 0 && (
        <div className="rounded-xl border border-gray-800 p-10 text-center text-slate-600"
             style={{ background: '#111827' }}>
          No orders match this filter.
        </div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="rounded-xl border border-gray-800 p-10 text-center text-slate-600"
             style={{ background: '#111827' }}>
          No orders in range.
        </div>
      )}

      {filtered.length > 0 && (
        <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ background: '#111827' }}>
          <div className="overflow-x-auto" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
              <thead>
                <tr>
                  {columns.map(h => (
                    <th key={h} style={{
                      ...THEAD_CELL,
                      textAlign: h === 'Asset' ? 'left' : 'right',
                      // Expires At de-emphasized — rarely something you act on at a
                      // glance, per the "don't give every column equal weight" note.
                      opacity: h === 'Expires At' ? 0.6 : 1,
                    }}>
                      {h.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const style = CATEGORY_STYLE[o.category] || { border: 'transparent', text: '#64748b' }
                  return (
                    <tr key={o.id}
                      style={{
                        borderBottom: '1px solid rgba(30,41,59,0.4)',
                        borderLeft: `3px solid ${style.border}`,
                      }}
                      className="hover:bg-slate-900 transition-colors">
                      <td style={{ padding: '6px 10px', color: '#e2e8f0', fontWeight: 700 }}>{o.symbol}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#94a3b8' }}>{o.order_type}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                        {o.side}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: style.text, fontWeight: 700 }}>
                        {o.pnl_dollar != null
                          ? `${o.pnl_dollar >= 0 ? '+' : ''}$${o.pnl_dollar.toFixed(2)}`
                          : '—'}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: style.text, fontWeight: 700 }}>
                        {o.pnl_pct != null
                          ? `${o.pnl_pct >= 0 ? '+' : ''}${o.pnl_pct.toFixed(2)}%`
                          : '—'}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#64748b' }}>{fmtQty(o.qty)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#64748b' }}>{fmtQty(o.filled_qty)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#94a3b8' }}>{fmtPrice(o.avg_fill_price)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#94a3b8' }}>
                        {o.status}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {fmtDateTime(o.submitted_at)}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {fmtDateTime(o.filled_at)}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#475569', whiteSpace: 'nowrap', opacity: 0.6 }}>
                        {fmtDateTime(o.expires_at)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem', borderTop: '1px solid #1e293b' }}>
            {filtered.length} of {orders.length} orders shown
          </div>
        </div>
      )}
    </div>
  )
}
